import { Trans } from "@lingui/react/macro";
import styles from "./RoutesAndActivities.module.scss";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Route from "./Route";
import NoResults from "@/components/NoResults/NoResults";
import { deducedError } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { ActivityItem } from "@/pages/ambulating/RoutesAndActivities/ActivityItem";
import { DaySwitcher } from "@/components/DaySwitcher/DaySwitcher";
import { SelectedDateHeading } from "./SelectedDateHeading";
import {
  useSelectedDate,
  useSelectedDateKey,
  useSetSelectedDate,
} from "@/Utils/useSelectedDate";
import { useMyRoutesAndActivities } from "@/api/Shifts";
import { nameRoute } from "@/api/Routes";
import { Tabs, TabPanel } from "react-aria-components";
import { GroupItem } from "./GroupItem";
import {
  activityOccurrenceStatusSchema,
  getUniqueId,
  groupStatusSchema,
} from "@medoma/models/activities";
import { routeStatusSchema } from "@medoma/models/routes";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftItem,
} from "@medoma/models/shiftWithContents";

const isFinished = (item: IShiftItem) => {
  const shiftItem = classifyShiftItem(item);
  switch (shiftItem.kind) {
    case shiftItemKindSchema.enum.route:
      return shiftItem.item.status === routeStatusSchema.enum.finished;
    case shiftItemKindSchema.enum.group:
      return shiftItem.item.visitStatus === groupStatusSchema.enum.finished;
    case shiftItemKindSchema.enum.occurrence:
      return (
        shiftItem.item.status === activityOccurrenceStatusSchema.enum.finished
      );
    default:
      throw shiftItem satisfies never;
  }
};

const itemKey = (item: IShiftItem) => {
  const shiftItem = classifyShiftItem(item);
  switch (shiftItem.kind) {
    case shiftItemKindSchema.enum.route:
      return shiftItem.item.id;
    case shiftItemKindSchema.enum.group:
    case shiftItemKindSchema.enum.occurrence:
      return getUniqueId(shiftItem.item);
    default:
      throw shiftItem satisfies never;
  }
};

const ShiftItem = ({ item }: { item: IShiftItem }) => {
  const shiftItem = classifyShiftItem(item);
  switch (shiftItem.kind) {
    case shiftItemKindSchema.enum.route:
      return <Route initialRoute={nameRoute(shiftItem.item)} />;
    case shiftItemKindSchema.enum.group:
      return <GroupItem group={shiftItem.item} />;
    case shiftItemKindSchema.enum.occurrence:
      return <ActivityItem activity={shiftItem.item} />;
    default:
      throw shiftItem satisfies never;
  }
};

function RoutesAndActivitiesContent() {
  const { t } = useLingui();
  const selectedDate = useSelectedDate();

  const {
    data: myItems,
    isPending,
    isError,
    error,
  } = useMyRoutesAndActivities(selectedDate);

  if (isPending) {
    return <Loading message={t`Hämtar dina rutter och aktiviteter`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta dina rutter och aktiviteter.`} ${deducedError(error)}`}
      />
    );
  }

  const unfinishedItems = myItems.filter((item) => !isFinished(item));
  const finishedItems = myItems.filter(isFinished);

  return (
    <>
      <section>
        <ul className={styles.routesList}>
          {unfinishedItems.length === 0 ? (
            <NoResults message={t`Inga planerade besök eller aktiviteter`} />
          ) : (
            unfinishedItems.map((item) => (
              <li key={itemKey(item)}>
                <ShiftItem item={item} />
              </li>
            ))
          )}
        </ul>
      </section>
      {finishedItems.length > 0 ? (
        <section>
          <Heading level="h2">
            <Trans>Utfört</Trans>
          </Heading>
          <ul className={styles.activitiesList}>
            {finishedItems.map((item) => (
              <li key={itemKey(item)}>
                <ShiftItem item={item} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function RoutesAndActivities() {
  const selectedDateKey = useSelectedDateKey();
  const setSelectedDate = useSetSelectedDate();

  return (
    <Tabs
      className={styles.routesAndActivities}
      selectedKey={selectedDateKey}
      onSelectionChange={(key) => setSelectedDate(String(key))}
    >
      <div className={styles.shiftHeader}>
        <Heading level="h1">
          <Trans>Mitt arbetspass</Trans>
        </Heading>
        <DaySwitcher />
      </div>
      <TabPanel id={selectedDateKey} className={styles.tabPanel}>
        <SelectedDateHeading />
        <RoutesAndActivitiesContent />
      </TabPanel>
    </Tabs>
  );
}

export default RoutesAndActivities;
